/**
 * The groups an opening can be filled from, before anybody is named.
 *
 * Pure, like `slotCandidates`. A drawer that opens straight onto three hundred
 * ranked names asks the wrong question first: the practice decides what kind of
 * appointment this chair should take before deciding which patient, and a
 * cohort with a count and a value beside it answers that in one glance. Naming
 * people is the second step, not the first.
 */

import { CandidateSource, SlotCandidate } from './slotCandidates'

export type Cohort = {
  /** The band's own name, in the practice's words: "Long past due". */
  label: string
  source: CandidateSource
  /**
   * Everybody in the queue, which is not the same as everybody in hand: the
   * queues are read a hundred deep and a treatment band can hold thousands.
   */
  count: number
  /** How many of them were actually read, and so how many can be named. */
  read: number
  /** Production the read part of this cohort represents, when it is priced. */
  value: number
  /**
   * What one booking is worth, only when every case in the band is worth the
   * same. The hygiene queues price every patient at the location's average
   * visit, so that figure is real. Treatment cases are each their own number,
   * from a few hundred to seventy-odd thousand, and calling the first one "per
   * booking" would report the best case in the band as the typical one.
   */
  perBooking?: number
  /** The largest single case in the band, which is honest whatever the spread. */
  topValue: number
  /**
   * How many carry a mobile number, so the panel can say what it can act on.
   *
   * Only when the whole cohort was read. Counting the mobile numbers in a
   * hundred rows and printing the answer beside a count of two thousand would
   * be a precise-looking figure about a sample, which is worse than saying
   * nothing.
   */
  reachable?: number
}

/**
 * Group the candidates into the queues they came from.
 *
 * Ordered by what one booking is worth rather than by how many people are in
 * the band. A cohort of four thousand cleanings and a cohort of ten crowns are
 * not comparable by size, and sorting by size would bury the ten every time.
 */
export function cohortsFor(candidates: SlotCandidate[], totals?: Map<string, number>): Cohort[] {
  // `count` and `reachable` are not accumulated: both are decided once the
  // whole read is in hand, against the queue's own total.
  const groups = new Map<string, { cohort: Omit<Cohort, `count` | `reachable`>; values: number[]; reachable: number }>()
  for (const candidate of candidates) {
    const value = candidate.value ?? 0
    const existing = groups.get(candidate.sourceLabel)
    if (!existing) {
      groups.set(candidate.sourceLabel, {
        cohort: {
          label: candidate.sourceLabel,
          source: candidate.source,
          read: 1,
          value,
          topValue: value,
        },
        values: [value],
        reachable: candidate.phone ? 1 : 0,
      })
      continue
    }
    existing.cohort.read += 1
    existing.cohort.value += value
    existing.cohort.topValue = Math.max(existing.cohort.topValue, value)
    if (candidate.phone) existing.reachable += 1
    existing.values.push(value)
  }
  return [...groups.values()]
    .map(({ cohort, values, reachable }) => {
      // The queue's own count when the caller has one, because a cohort card
      // that reported the read depth would tell the practice that every band is
      // a hundred people deep and that its biggest is the size of its smallest.
      const count = totals?.get(cohort.label) ?? cohort.read
      return {
        ...cohort,
        count,
        // Claimed only when the band really is one number. Averaging a spread of
        // treatment cases would invent a typical patient who does not exist.
        perBooking: values.every((value) => value === values[0]) ? values[0] || undefined : undefined,
        reachable: cohort.read >= count ? reachable : undefined,
      }
    })
    .sort((a, b) => b.topValue - a.topValue || b.count - a.count)
}

/** The people in one cohort, in the order the ranking already put them. */
export function candidatesIn(candidates: SlotCandidate[], label: string): SlotCandidate[] {
  return candidates.filter((candidate) => candidate.sourceLabel === label)
}
